import React, { Component } from 'react';
import HttpRunner from '../query';
import urlHandler from '../../../utils/urlHandler';
import perms from '../../../utils/permissions';
import examples from '../../../utils/examples';

class DocV1Usage extends Component {
    constructor(props){
        super(props);

        this.state = {
            dynamicSample: false
        }
    }

    componentDidMount() {
    }

    render() {
        return (
            <div className="bucket" id="top">
                <h3 className="heading-1">Usage Information</h3>
                <code className="api-definition">
                    <span className="get-badge">GET</span>&nbsp;{urlHandler.API_ENDPOINT_V1_USAGE}?{'{'}query parameters{'}'}
                </code>
                <div className="description">
                    The usage endpoint provides an interface for customers to check the API usage for their account or userid.<br/>
                </div>

                <h4 className="heading-2">Request</h4>

                <h4 className="heading-3">Query Parameters</h4>
                <div className="description">
                    The usage endpoint allows you to specify the period of usage (begin, end), the interval (hourly or daily) and 
                    level, which can be a specific user key or all user keys for an account. The following query parameters 
                    are supported for the usage request.
                </div>
                <table className="table-wrapper enrich">
                    <thead className="table-header enrich">
                    <tr>
                        <th>Name </th>
                        <th>Description </th>
                        <th>Type </th>
                        <th>Notes </th>
                    </tr>
                    </thead>

                    <tbody className="table-body">
                    <tr>
                        <td className="attribute">begin</td>
                        <td className="meaning">
                            Timestamp for the beginning date of the usage report.<br/>
                            <br/>
                            Format: YYYY-MM-DDThh:mm:ssZ<br/>
                            <br/>
                            <i>
                            YYYY – Year<br/>
                            MM – Month<br/>
                            DD – Day<br/>
                            T – time separator. T required if optional time is supplied.<br/>
                            hh – Hour<br/>
                            mm – Minute<br/>
                            ss – second<br/>
                            Z – UTC time zone indicator. Usage data is collected and reported in UTC.<br/>
                            </i>
                        </td>
                        <td className="type">String<br/>
                            <br/>
                            Example: 2019-04-22T01:00:00Z
                        </td>
                        <td className="type">Required.<br/>
                            <br/>
                            If time is not supplied, T00:00:00Z is appended to the requested date.
                        </td>
                    </tr>
                    <tr>
                        <td className="attribute">end</td>
                        <td className="meaning">
                            Timestamp for the end date of the usage report. Same format as <i>begin</i> parameter.<br/>
                        </td>
                        <td className="type">String<br/>
                            <br/>
                            Example: 2019-04-22T20:00:00Z
                        </td>
                        <td className="type">Optional. Default: Current date and time<br/>
                            <br/>
                            If date is supplied, but not time, then 23:59:59Z is appended to the requested date.
                        </td>
                    </tr>
                    <tr>
                        <td className="attribute">interval</td>
                        <td className="meaning">
                            Interval for the usage report data. <i>hourly</i> or <i>daily</i> are the supported intervals.
                        </td>
                        <td className="type">String</td>
                        <td className="type">Optional. Default: <i>hourly</i>
                        </td>
                    </tr>
                    <tr>
                        <td className="attribute">level</td>
                        <td className="meaning">
                            The level of the usage report returned.<br/>
                            <br/>
                            <i>account</i> returns usage data for all user keys of the current user’s account.<br/>
                            <br/>
                            <i>user</i> returns usage data for the current user’s key or for a different user’s key 
                                if the user parameter is supplied on the query.<br/>
                            <br/>
                            Users must be authorized to access account level data or data about another user id.
                        </td>
                        <td className="type">String</td>
                        <td className="type">Optional. Default: <i>account</i>
                        </td>
                    </tr>
                    <tr>
                        <td className="attribute">user</td>
                        <td className="meaning">
                            Limits the usage report data to only include data for the requested user key. Only valid 
                            for <i>user</i> level usage requests.<br/>
                            <br/>
                            Users must be authorized to access data about another user id.
                        </td>
                        <td className="type">String</td>
                        <td className="type">Optional. Default: Requesting user's id.
                        </td>
                    </tr>
                    </tbody>
                </table>

                <h4 className="heading-2">Response</h4>
                <div className="description">
                    The usage response object contains the detailed API usage for each endpoint called. The results will contain 
                    the following fields.
                </div>

                <h4 className="heading-3">Response object</h4>

                <table className="table-wrapper enrich">
                    <thead className="table-header enrich">
                    <tr>
                        <th>Name </th>
                        <th>Description </th>
                        <th>Type </th>
                        <th>Notes </th>
                    </tr>
                    </thead>

                    <tbody className="table-body">
                    <tr>
                        <td className="attribute">accountId</td>
                        <td className="meaning">The account id for the requested usage.
                        </td>
                        <td className="type">String</td>
                        <td className="type">Optional</td>
                    </tr>
                    <tr>
                        <td className="attribute">requestedKey</td>
                        <td className="meaning">The key for the usage request. Defaults to the account or user id of the requesting user, 
                            if the optional <i>user</i> parameter was not supplied on the request.
                        </td>
                        <td className="type">String</td>
                        <td className="type"></td>
                    </tr>
                    <tr>
                        <td className="attribute">requestLevel</td>
                        <td className="meaning">The level of report data requested. Either <i>account</i> or <i>user</i>.
                        </td>
                        <td className="type">String</td>
                        <td className="type"></td>
                    </tr>
                    <tr>
                        <td className="attribute">interval</td>
                        <td className="meaning">The interval which the usage data is reported. Either <i>hourly</i> or <i>daily</i>.
                        </td>
                        <td className="type">String</td>
                        <td className="type"></td>
                    </tr>
                    <tr>
                        <td className="attribute">begin</td>
                        <td className="meaning">The timestamp for the beginning of the usage report.
                        </td>
                        <td className="type">String</td>
                        <td className="type"></td>
                    </tr>
                    <tr>
                        <td className="attribute">end</td>
                        <td className="meaning">The timestamp for the end of the usage report.
                        </td>
                        <td className="type">String</td>
                        <td className="type"></td>
                    </tr>
                    <tr>
                        <td className="attribute">usage</td>
                        <td className="meaning">JSON object containing the usage data. If not included in the response, the no usage 
                            data was found for the requested period.<br/>
                            <br/>
                            The keys to the object are the user ids which had usage and met the other 
                            request parameters. The user’s usage data is reported by environment and endpoint.
                        </td>
                        <td className="type">JSON Object</td>
                        <td className="type"></td>
                    </tr>
                    <tr>
                        <td className="attribute">environments</td>
                        <td className="meaning">JSON object containing the usage data from a specific operational environment for the 
                            containing user id object. The key indicates which operational environment the usage occurred in. i.e. <i>prod</i> for 
                            production environment and <i>test</i> for integration testing environment.
                        </td>
                        <td className="type">JSON Object</td>
                        <td className="type"></td>
                    </tr>
                    <tr>
                        <td className="attribute">endpoints</td>
                        <td className="meaning">JSON object containing the usage data for a specific API endpoint from the containing 
                            environment object. The key indicates the path of the used endpoint.
                        </td>
                        <td className="type">JSON Object</td>
                        <td className="type"></td>
                    </tr>
                    <tr>
                        <td className="attribute">transactionSummaries</td>
                        <td className="meaning">JSON object containing the detailed usage data from a specific time period for the 
                            containing endpoint. The key indicates the time period represented by the usage data.<br/>
                            <br/>
                            If the key is for a date range, then the data is a roll-up of the usage data for the period of time.<br/>
                            <br/>
                            If the key is only a date, then the usage summary data is a roll-up of the data for that full day.<br/>
                            <br/>
                            If the key is a full date/time timestamp, then the usage summary data is for a specific hour of usage.<br/>
                            <br/>
                            The transaction summary data is reported for a specific endpoint by a specific user during a specific 
                            time period. The endpoint, user, and time period are determined by the keys of the parent objects containing 
                            the transaction data.
                        </td>
                        <td className="type">JSON Object</td>
                        <td className="type"></td>
                    </tr>
                    <tr>
                        <td className="attribute">transactionCount</td>
                        <td className="meaning">The number of requests to the endpoint.
                        </td>
                        <td className="type">Integer</td>
                        <td className="type"></td>
                    </tr>
                    <tr>
                        <td className="attribute">averageResponseTime</td>
                        <td className="meaning">The average time to process a request (in milliseconds).
                        </td>
                        <td className="type">Float</td>
                        <td className="type"></td>
                    </tr>
                    <tr>
                        <td className="attribute">maxResponseTime</td>
                        <td className="meaning">The maximum time to process a request (in milliseconds).
                        </td>
                        <td className="type">Float</td>
                        <td className="type"></td>
                    </tr>
                    <tr>
                        <td className="attribute">minResponseTime</td>
                        <td className="meaning">The minimum time to process a request (in milliseconds).
                        </td>
                        <td className="type">Float</td>
                        <td className="type"></td>
                    </tr>
                    <tr>
                        <td className="attribute">averageResponseSize</td>
                        <td className="meaning">The average size of a response from the endpoint for a request (in bytes).
                        </td>
                        <td className="type">Float</td>
                        <td className="type"></td>
                    </tr>
                    <tr>
                        <td className="attribute">maxResponseSize</td>
                        <td className="meaning">The maximum size of a response from the endpoint for a request (in bytes).
                        </td>
                        <td className="type">Float</td>
                        <td className="type"></td>
                    </tr>
                    <tr>
                        <td className="attribute">minResponseSize</td>
                        <td className="meaning">The minimum size of a response from the endpoint for a request (in bytes).
                        </td>
                        <td className="type">Float</td>
                        <td className="type"></td>
                    </tr>
                    <tr>
                        <td className="attribute">averageRequestSize</td>
                        <td className="meaning">The average size of a request to the endpoint (in bytes).
                        </td>
                        <td className="type">Float</td>
                        <td className="type"></td>
                    </tr>
                    <tr>
                        <td className="attribute">maxRequestSize</td>
                        <td className="meaning">The maximum size of a request to the endpoint (in bytes).
                        </td>
                        <td className="type">Float</td>
                        <td className="type"></td>
                    </tr>
                    <tr>
                        <td className="attribute">minRequestSize</td>
                        <td className="meaning">The minimum size of a request to the endpoint (in bytes).
                        </td>
                        <td className="type">Float</td>
                        <td className="type"></td>
                    </tr>
                    </tbody>
                </table>

                
                <div className="heading-2">Example</div>
                <div className="example-content host">
                    <pre>
                        GET {urlHandler.API_ENDPOINT_V2_ENTITIES}?begin=2019-04-01&end=2019-04-02&interval=daily&level=user&user=some-userid<br/>
                        Accept: application/json<br/>
                        Authorization: Bearer [YOUR-API-KEY]<br/>
                    </pre>
                </div>
                <HttpRunner query="searchByName" 
                    response={!this.state.dynamicSampleSearch ? JSON.stringify(examples.EXAMPLE_RESPONSE_V1_USAGE) : null}
                />
            </div>
        );
    }
}

export default DocV1Usage;